import type { PickPageCollectionNodeFragment } from '~/api/shopify/fragments/pickPageCollection.generated';
import type { PickPageArticleNodeFragment } from '~/api/shopify/fragments/pickPageArticle.generated';
import type { PickPageProductNodeFragment } from '~/api/shopify/fragments/pickPageProduct.generated';
import type { PickPagePageNodeFragment } from '~/api/shopify/fragments/pickPagePage.generated';
import type { IPickPageItem, IPickPageMode } from '../types';

import {
  PICK_PAGE_PATH_TRAILING_SLASH_PATTERN,
  PICK_PAGE_QUERYABLE_STATUSES,
  PICK_PAGE_ADMIN_SECTION,
  PICK_PAGE_HOME_PATH,
  PICK_PAGE_HOME_ID,
} from '../constants';
import { SHOPIFY_ONLINE_STORE_APP_HANDLE } from '~/constants/shopify';
import { getShopifyPageLink } from '~/modules/page-analytic/helpers';
import { toRenderedTemplateSuffix } from '~/utils';
import { TemplateType } from '~/types/template';
import { IPickPageType } from '../types';

export interface IToPickPageItemParams<TNode> {
  node: TNode;
  /**
   * The theme's templates, to resolve the one this page renders. Absent when the caller holds none —
   * the page then carries no template, and no template test can be matched against it.
   */
  templateAssetKeys?: Set<string>;
}

export interface IIsPickPageOnlineStoreAvailableParams {
  page: IPickPageItem;
  /** What the pick is for — a product's status qualifies per mode, see `PICK_PAGE_QUERYABLE_STATUSES`. */
  mode: IPickPageMode;
  isControl: boolean;
}

export interface IToPickPageHomeItemParams {
  title: string;
  /**
   * The template the storefront serves at `/` — see `toRenderedHomeTemplateSuffix`. The homepage has
   * no record to read one off, so it is resolved from the theme and handed in. Undefined carries no
   * template, which matches no template experiment against the row.
   */
  templateSuffix?: string;
}

// Shopify names every record with a GID; only a product's pick is stored that way, the other three
// carry the plain id. A pick is matched against a stored one by this exact string — see
// `toPickPageMatchKey` — so a listed row has to keep spelling it the way the saved campaigns do.
const toPlainPlatformId = (baseID: string): string => baseID.split('/').pop() ?? baseID;

// The storefront has exactly one homepage, so it is a fixed row rather than a query.
export const toPickPageHomeItem = ({
  title,
  templateSuffix,
}: IToPickPageHomeItemParams): IPickPageItem => ({
  id: PICK_PAGE_HOME_ID,
  baseID: '',
  title,
  url: getShopifyPageLink({ type: TemplateType.HOME }) ?? '',
  type: IPickPageType.HOME,
  templateSuffix,
});

export const toPickPageItemFromPage = ({
  node,
  templateAssetKeys,
}: IToPickPageItemParams<PickPagePageNodeFragment>): IPickPageItem => ({
  id: toPlainPlatformId(node.id),
  baseID: toPlainPlatformId(node.id),
  handle: node.handle,
  title: node.title,
  isPublished: node.isPublished,
  url: getShopifyPageLink({ handle: node.handle, type: TemplateType.PAGE }) ?? '',
  type: IPickPageType.LANDING,
  templateSuffix: toRenderedTemplateSuffix({
    type: TemplateType.PAGE,
    templateSuffix: node.templateSuffix,
    templateAssetKeys,
  }),
});

/**
 * Whether the storefront serves a collection. A collection carries no visibility of its own — it is
 * published per sales channel — so the answer is whether the Online Store is among the channels it
 * is published to.
 */
const isCollectionOnOnlineStore = (node: PickPageCollectionNodeFragment): boolean =>
  node.resourcePublications.nodes.some(
    (resourcePublication) =>
      resourcePublication.publication.app.handle === SHOPIFY_ONLINE_STORE_APP_HANDLE,
  );

export const toPickPageItemFromCollection = ({
  node,
  templateAssetKeys,
}: IToPickPageItemParams<PickPageCollectionNodeFragment>): IPickPageItem => ({
  id: toPlainPlatformId(node.id),
  baseID: toPlainPlatformId(node.id),
  handle: node.handle,
  title: node.title,
  image: node.image?.url,
  isPublished: isCollectionOnOnlineStore(node),
  url: getShopifyPageLink({ handle: node.handle, type: TemplateType.COLLECTION }) ?? '',
  type: IPickPageType.COLLECTION,
  templateSuffix: toRenderedTemplateSuffix({
    type: TemplateType.COLLECTION,
    templateSuffix: node.templateSuffix,
    templateAssetKeys,
  }),
});

// An article's path is nested under the blog holding it, which every article has exactly one of.
export const toPickPageItemFromArticle = ({
  node,
  templateAssetKeys,
}: IToPickPageItemParams<PickPageArticleNodeFragment>): IPickPageItem => ({
  id: toPlainPlatformId(node.id),
  baseID: toPlainPlatformId(node.id),
  handle: node.handle,
  title: node.title,
  image: node.image?.url,
  isPublished: node.isPublished,
  url:
    getShopifyPageLink({
      handle: node.handle,
      type: TemplateType.ARTICLE,
      blogHandle: node.blog.handle,
    }) ?? '',
  type: IPickPageType.BLOG_POST,
  templateSuffix: toRenderedTemplateSuffix({
    type: TemplateType.ARTICLE,
    templateSuffix: node.templateSuffix,
    templateAssetKeys,
  }),
});

export const toPickPageItemFromProduct = ({
  node,
  templateAssetKeys,
}: IToPickPageItemParams<PickPageProductNodeFragment>): IPickPageItem => ({
  // The one type whose baseID stays a GID: that is the string a product pick is stored as.
  id: node.id,
  baseID: node.id,
  handle: node.handle,
  title: node.title,
  image: node.featuredImage?.url,
  status: node.status,
  publishedAt: node.publishedAt,
  url: getShopifyPageLink({ handle: node.handle, type: TemplateType.PRODUCT }) ?? '',
  type: IPickPageType.PRODUCT,
  templateSuffix: toRenderedTemplateSuffix({
    type: TemplateType.PRODUCT,
    templateSuffix: node.templateSuffix,
    templateAssetKeys,
  }),
});

/**
 * Whether the storefront actually serves this page — the one thing a page has to be for a test to
 * run on it, read by both a listed row and a typed URL.
 *
 * A page, an article and a collection answer with `isPublished`: the first two carry a visibility of
 * their own, a collection is published per sales channel, and the field reports the Online Store for
 * all three. Null means a platform service that predates the field rather than an answer, so it
 * reads as available. A product answers with `publishedAt` instead — `status` doesn't tell "off the
 * Online Store channel" from "draft" — plus a status the mode accepts on this side of the test.
 */
export const isPickPageOnlineStoreAvailable = ({
  page,
  mode,
  isControl,
}: IIsPickPageOnlineStoreAvailableParams): boolean => {
  // The storefront always serves a homepage, and there is no record behind it to publish.
  if (page.type === IPickPageType.HOME) return true;
  if (page.type !== IPickPageType.PRODUCT) return page.isPublished !== false;
  if (!page.publishedAt) return false;

  const availableStatuses = PICK_PAGE_QUERYABLE_STATUSES[mode][isControl ? 'control' : 'variant'];

  return !!page.status && availableStatuses.includes(page.status);
};

/**
 * Admin path of the record behind a page, so an unavailable one can offer to open it. `baseID` is a
 * platform GID for a product and a plain id for the other types, and its trailing segment is the
 * numeric id an admin path takes either way.
 */
export const toPickPageAdminPath = (page: IPickPageItem): string | undefined => {
  const section = PICK_PAGE_ADMIN_SECTION[page.type];
  const id = toPlainPlatformId(page.baseID);
  if (!section || !id) return undefined;

  return `${section}/${id}`;
};

// A product's baseID stays a GID (gid://shopify/Product/123); Shopify admin paths use the numeric id.
export const toPlatformProductAdminPath = (baseID: string): string =>
  `products/${toPlainPlatformId(baseID)}`;

const PICK_PAGE_TYPE_BY_PATH_PREFIX: Array<{ prefix: string; type: IPickPageType }> = [
  { prefix: '/products/', type: IPickPageType.PRODUCT },
  { prefix: '/collections/', type: IPickPageType.COLLECTION },
  { prefix: '/blogs/', type: IPickPageType.BLOG_POST },
];

/** Reads a stored URL as the storefront path it points at — what a pick is labelled with. */
export const toPickPagePath = (url?: string | null): string =>
  (url ?? '').replace(/^https?:\/\/[^/]+/i, '').split('?')[0];

/**
 * The path two pages are the same page by. A stored destination and a listed row spell the same page
 * differently — one absolute, one relative, either with a trailing slash — so both are reduced to one
 * shape before they are matched. An input naming no path at all reads as the homepage, which is the
 * page a bare domain serves.
 */
export const toPickPageMatchPath = (url?: string | null): string =>
  toPickPagePath(url).replace(PICK_PAGE_PATH_TRAILING_SLASH_PATTERN, '') || PICK_PAGE_HOME_PATH;

/**
 * Reads a stored URL back as the type that produced it. Needed because a pick is persisted as its
 * URL alone — no field records which tab it came from.
 */
export const toPickPageTypeFromUrl = (url?: string | null): IPickPageType => {
  const path = toPickPagePath(url);
  if (!path || path === '/') return IPickPageType.HOME;

  return (
    PICK_PAGE_TYPE_BY_PATH_PREFIX.find(({ prefix }) => path.startsWith(prefix))?.type ??
    IPickPageType.LANDING
  );
};
